import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Alert, OwnedAssetService } from '../../services';
import { CloseButtonSvgIcon, OnButtonLoaderIcon } from '../icons/IconProvider';
import FormInput from '../FormInput';

const AddInvestment = ({
  isOpen,
  onClose,
  allProjectsList,
  userId,
  ownedAssetList,
  getUserDetails,
  setRefreshTrigger,
}) => {
  const [investmentLoader, setInvestmentLoader] = useState(false);
  const [transactionDate, setTransactionDate] = useState(null);
  const [availableQuantity, setAvailableQnty] = useState(0);
  const [projectPrice, setProjectPrice] = useState(0);

  const schema = (availableQnty) =>
    Yup.object({
      project: Yup.string().required('Project is required').nullable(),
      quantity: Yup.number()
        .typeError('Quantity is required')
        .required('Quantity is required')
        .max(
          availableQnty,
          `Enter token quantity should not exceed the available quantity ${availableQnty}`,
        )
        .nullable(),
      paymentMethod: Yup.string()
        .required('Payment method is required')
        .nullable(),
      transactionId: Yup.string()
        .max(255, 'Transaction ID cannot exceed 255 characters')
        .nullable(),
      transactionDate: Yup.string().required('Transaction date is required.'),
    }).required();

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema(availableQuantity)),
  });

  const quantity = watch('quantity');

  useEffect(() => {
    if (quantity && projectPrice) {
      const calculatedValue = Number(quantity) * projectPrice;
      setValue('investedAmount', calculatedValue);
    } else {
      setValue('investedAmount', 0);
    }
  }, [quantity, projectPrice, setValue]);

  const addInvestment = async (_input) => {
    try {
      setInvestmentLoader(true);
      const data = {
        ..._input,
        userId,
      };
      let response = await OwnedAssetService.addInvestment(data);
      if (response.status === 'failure') {
        setInvestmentLoader(false);
        Alert.error(response.message);
        return;
      }
      onClose();
      Alert.success(response.message);
      setInvestmentLoader(false);
      ownedAssetList();
      getUserDetails(userId);
      reset();
      setTransactionDate(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      setInvestmentLoader(false);
      Alert.error(error.message);
    }
  };

  if (!isOpen) return null;

  const paymentMethods = [
    {
      value: 'invest_payment_link',
      label: 'Invest payment link',
    },
    {
      value: 'mollie_payment_link',
      label: 'Mollie payment link',
    },
    {
      value: 'sepa_transfer',
      label: 'SEPA direct transfer',
    },
    { value: 'other', label: 'Other' },
  ];

  return (
    <>
      <div>
        <div className="justify-center flex items-center overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
          <div className="relative w-full mx-3 my-4 max-w-md">
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
              <div className="flex justify-between items-center py-3 px-6">
                <h3>Add Investment</h3>
                <button
                  className="p-1 ml-auto bg-transparent border-0 text-black text-3xl leading-none font-semibold outline-none focus:outline-none absolute right-1 top-3 z-10 flex justify-center items-center"
                  onClick={() => {
                    onClose();
                    reset();
                    setTransactionDate(null);
                  }}
                >
                  <CloseButtonSvgIcon />
                </button>
              </div>
              <form onSubmit={handleSubmit(addInvestment)}>
                <div className="py-3 px-6">
                  <FormInput
                    label="Project"
                    name="project"
                    type="select"
                    register={register}
                    errors={errors}
                    required
                    options={allProjectsList.map((p) => ({
                      value: p.id,
                      label: p.name,
                    }))}
                    onChange={(e) => {
                      const selectedProject = allProjectsList.find(
                        (project) => project.id === Number(e.target.value),
                      );
                      setProjectPrice(selectedProject?.price || 0);
                      setAvailableQnty(selectedProject?.available_qty || 0);
                      setValue('quantity', '');
                    }}
                  />

                  <div className="mb-4">
                    <FormInput
                      label="Quantity"
                      name="quantity"
                      type="number"
                      placeholder="Quantity"
                      register={register}
                      errors={errors}
                      step={1}
                    />
                  </div>
                  <div className="mb-4">
                    <Controller
                      name="investedAmount"
                      control={control}
                      defaultValue={0}
                      render={({ field }) => (
                        <FormInput
                          value={field.value}
                          label="Transaction value (€)"
                          type="number"
                          readOnly
                        />
                      )}
                    />
                  </div>
                  <div className="mb-4">
                    <FormInput
                      label="Payment Method"
                      name="paymentMethod"
                      type="select"
                      register={register}
                      errors={errors}
                      required
                      options={paymentMethods}
                    />
                  </div>
                  <div className="mb-4">
                    <FormInput
                      label="Transaction ID"
                      name="transactionId"
                      type="text"
                      placeholder="Enter transaction ID or bank transfer note"
                      maxLength={255}
                      register={register}
                      errors={errors}
                    />
                  </div>

                  {/* Transaction Date */}
                  <div className="mb-4">
                    <FormInput
                      type="date-picker"
                      label="Transaction Date"
                      name="transactionDate"
                      control={control}
                      errors={errors}
                      required
                      placeholder="Select transaction date"
                      value={transactionDate}
                      onChange={(date) => {
                        setValue(
                          'transactionDate',
                          date.startDate ? date.startDate : '',
                          {
                            shouldValidate: true,
                            shouldDirty: true,
                          },
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="pb-6 px-6 text-center">
                  <button
                    type="submit"
                    className={
                      investmentLoader
                        ? 'bg-[#787a7d] cursor-not-allowed text-brandBlue border border-[#787a7d] font-bold uppercase text-sm px-5 py-3 rounded-md w-auto inline-block shadow hover:shadow-lg outline-none focus:outline-none mb-1 ease-linear transition-all duration-150'
                        : 'bg-GreenColor text-brandBlue text-base font-semibold px-5 py-3 rounded-md'
                    }
                    disabled={investmentLoader}
                  >
                    {investmentLoader ? (
                      <div className="flex items-center gap-2">
                        <OnButtonLoaderIcon />
                        Adding
                      </div>
                    ) : (
                      'Add'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
      </div>
    </>
  );
};

export default AddInvestment;
